/**
 * Metodi di pagamento che il TA rende disponibili al cliente su una proposta.
 *
 *   - `stripe`        : il Checkout di sempre (carta, Klarna, bonifico Stripe...);
 *   - `bank_transfer` : bonifico sul conto di chi incassa, con le coordinate del TA.
 *                       Non passa da Stripe: l'incasso si registra a mano con
 *                       "segna come pagata" (metodo `bank_transfer`).
 *
 * Senza configurazione (`configured: false`) vale solo Stripe: e' il comportamento
 * delle proposte inviate prima che la scelta esistesse.
 */
export const PROPOSAL_PAYMENT_METHODS = ['stripe', 'bank_transfer'] as const;

export type ProposalPaymentMethod = (typeof PROPOSAL_PAYMENT_METHODS)[number];

export interface IBankTransferDetails {
    bank_account_holder: string | null;
    bank_iban: string | null;
    bank_bic: string | null;
    bank_name: string | null;
    bank_reference: string | null;
    bank_instructions: string | null;
    /** NULL = testo standard tradotto (`paymentOptions.bank.defaultReceiptNote`). */
    bank_receipt_note: string | null;
}

export interface IPaymentOptions extends Partial<IBankTransferDetails> {
    configured: boolean;
    methods: ProposalPaymentMethod[];
    /** Solo per chi gestisce l'itinerario: coordinate predefinite di chi incassa. */
    defaults?: Partial<IBankTransferDetails> | null;
    /** Solo per chi gestisce l'itinerario: chi incassa ha Stripe attivo. */
    payee_stripe_active?: boolean;
}

/** Stato del form, comune a send-quote e alla modale pagamenti del builder. */
export interface IPaymentOptionsForm extends Record<keyof IBankTransferDetails, string> {
    methods: ProposalPaymentMethod[];
}

export interface IPaymentDefaults extends Partial<IBankTransferDetails> {
    /** false per il membro del team: le coordinate sono dell'agenzia. */
    can_edit: boolean;
}

export const DEFAULT_PAYMENT_OPTIONS: IPaymentOptions = { configured: false, methods: ['stripe'] };

const BANK_FIELDS: (keyof IBankTransferDetails)[] = [
    'bank_account_holder',
    'bank_iban',
    'bank_bic',
    'bank_name',
    'bank_reference',
    'bank_instructions',
    'bank_receipt_note',
];

const pickBank = (
    source: Partial<IBankTransferDetails> | null | undefined,
): Record<keyof IBankTransferDetails, string> =>
    Object.fromEntries(BANK_FIELDS.map((k) => [k, source?.[k] ?? ''])) as Record<keyof IBankTransferDetails, string>;

/**
 * Valori iniziali del form.
 *
 * Proposta gia' configurata (reinvio, modifica dal builder): si riparte da quella.
 * Mai configurata: tutti i metodi selezionati — la massima flessibilita' e' il
 * comportamento predefinito — e coordinate precompilate da quelle predefinite. Senza
 * coordinate predefinite il bonifico parte spento invece che con campi vuoti obbligatori.
 * Senza Stripe attivo, Stripe parte spento: il cliente non potrebbe usarlo.
 */
export const initialPaymentOptionsForm = (
    options: IPaymentOptions | null | undefined,
    stripeActive: boolean,
): IPaymentOptionsForm => {
    if (options?.configured) {
        const bank = options.methods.includes('bank_transfer') ? options : options.defaults;
        return { methods: [...options.methods], ...pickBank(bank) };
    }

    const defaults = options?.defaults ?? null;
    const hasDefaults = !!(defaults?.bank_account_holder && defaults?.bank_iban);
    const methods: ProposalPaymentMethod[] = [];
    if (stripeActive) methods.push('stripe');
    if (hasDefaults || !stripeActive) methods.push('bank_transfer');
    return { methods, ...pickBank(defaults) };
};

export const normalizeIban = (iban: string): string => iban.replace(/\s+/g, '').toUpperCase();

/** Formato ISO 13616 + controllo mod-97. Il database verifica solo il formato. */
export const isValidIban = (raw: string): boolean => {
    const iban = normalizeIban(raw);
    if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
    const rearranged = iban.slice(4) + iban.slice(0, 4);
    let remainder = 0;
    for (const ch of rearranged) {
        const digits = /[A-Z]/.test(ch) ? String(ch.charCodeAt(0) - 55) : ch;
        for (const d of digits) remainder = (remainder * 10 + Number(d)) % 97;
    }
    return remainder === 1;
};

/** IBAN a gruppi di 4, come lo si legge su un bollettino. */
export const formatIban = (iban: string | null | undefined): string =>
    normalizeIban(iban ?? '').replace(/(.{4})/g, '$1 ').trim();

export type PaymentOptionsFieldError = 'methods' | 'bank_account_holder' | 'bank_iban';

export const validatePaymentOptionsForm = (form: IPaymentOptionsForm): Set<PaymentOptionsFieldError> => {
    const errors = new Set<PaymentOptionsFieldError>();
    if (form.methods.length === 0) errors.add('methods');
    if (form.methods.includes('bank_transfer')) {
        if (!form.bank_account_holder.trim()) errors.add('bank_account_holder');
        if (!isValidIban(form.bank_iban)) errors.add('bank_iban');
    }
    return errors;
};

/** Payload di set_itinerary_payment_options: il server azzera le coordinate senza bonifico. */
export const toPaymentOptionsPayload = (form: IPaymentOptionsForm) => ({
    ...form,
    bank_iban: normalizeIban(form.bank_iban),
});

/** Il bonifico e' davvero utilizzabile: abilitato dal TA e con le coordinate minime. */
export const isBankTransferAvailable = (options: IPaymentOptions | null | undefined): boolean =>
    !!options?.methods.includes('bank_transfer') && !!options.bank_iban && !!options.bank_account_holder;
